import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const { t } = useTranslation('common');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      setSubmitMessage(t('footer.emailPlaceholder'));
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);

      const response = await fetch('{{NEWSLETTER_FORM_URL}}', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString()
      });

      if (response.ok) {
        setSubmitMessage(t('footer.subscribe'));
        setEmail('');
      } else {
        setSubmitMessage('Error');
      }
    } catch (error) {
      setSubmitMessage('Error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <footer id="iletisim" className="bg-[#0D0D0D] text-white mx-4 sm:mx-8 lg:mx-16 my-8 sm:my-12 lg:my-16 rounded-[24px] sm:rounded-[32px] lg:rounded-[40px] overflow-hidden">
        <div className="max-w-[1440px] mx-auto px-6 sm:px-8 md:px-12 lg:px-20 py-10 sm:py-12 lg:py-16">
          {/* Top Section */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-10 lg:gap-12 mb-10 sm:mb-12 lg:mb-16">
            {/* Left Column - Newsletter */}
            <div className="lg:col-span-5">
              <h3 className="text-3xl sm:text-4xl md:text-5xl font-thin leading-tight mb-4 sm:mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                {t('footer.newsletter')}
              </h3>
              <p className="text-[#B0B0B0] text-sm sm:text-base leading-relaxed mb-8 sm:mb-10 lg:mb-12" style={{ lineHeight: '1.6' }}>
                {t('footer.newsletterText')}
              </p>

              <form onSubmit={handleSubmit} data-readdy-form id="newsletter-form">
                <div className="mb-6 sm:mb-8">
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('footer.emailPlaceholder')}
                    className="w-full bg-transparent border-b border-white/30 text-white text-base sm:text-lg py-2 sm:py-3 focus:outline-none focus:border-white transition-colors placeholder:text-white/50"
                    disabled={isSubmitting}
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-white text-black px-8 sm:px-10 py-3 sm:py-4 rounded-full text-sm font-light tracking-wide hover:bg-gray-200 transition-all duration-300 flex items-center gap-2 sm:gap-3 whitespace-nowrap cursor-pointer disabled:opacity-50"
                >
                  {isSubmitting ? '...' : t('footer.subscribe')}
                  <i className="ri-arrow-right-line text-base sm:text-lg"></i>
                </button>
                {submitMessage && (
                  <p className={`mt-3 sm:mt-4 text-xs sm:text-sm ${submitMessage.includes('Error') ? 'text-red-400' : 'text-green-400'}`}>
                    {submitMessage}
                  </p>
                )}
              </form>
            </div>

            {/* Middle Column - Quick Links */}
            <div className="lg:col-span-3">
              <h4 className="text-xs sm:text-sm uppercase tracking-widest mb-4 sm:mb-6" style={{ letterSpacing: '2px' }}>
                {t('footer.quickLinks')}
              </h4>
              <ul className="space-y-3 sm:space-y-5">
                <li><a href="#" className="text-sm sm:text-base text-white/80 hover:text-white transition-colors underline cursor-pointer">{t('nav.home')}</a></li>
                <li><a href="#projeler" className="text-sm sm:text-base text-white/80 hover:text-white transition-colors underline cursor-pointer">{t('nav.projects')}</a></li>
                <li><a href="https://cresquo.com/" target="_blank" rel="noopener noreferrer" className="text-sm sm:text-base text-white/80 hover:text-white transition-colors underline cursor-pointer">{t('nav.furniture')}</a></li>
                <li><a href="#haberler" className="text-sm sm:text-base text-white/80 hover:text-white transition-colors underline cursor-pointer">{t('nav.news')}</a></li>
                <li>
                  <button 
                    onClick={() => setShowPrivacyModal(true)}
                    className="text-sm sm:text-base text-white/80 hover:text-white transition-colors underline cursor-pointer text-left"
                  >
                    {t('footer.privacy')}
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => setShowTermsModal(true)}
                    className="text-sm sm:text-base text-white/80 hover:text-white transition-colors underline cursor-pointer text-left"
                  >
                    {t('footer.terms')}
                  </button>
                </li>
              </ul>
            </div>

            {/* Right Column - Contact */}
            <div className="lg:col-span-4">
              <h4 className="text-xs sm:text-sm uppercase tracking-widest mb-4 sm:mb-6" style={{ letterSpacing: '2px' }}>
                {t('footer.contact')}
              </h4>
              <div className="space-y-3 sm:space-y-4 mb-8 sm:mb-10">
                <p className="text-base sm:text-lg text-white break-words">+90 541 416 79 16 / +90 542 583 07 36</p>
                <a href="mailto:ernado@outlook.com.tr" className="text-base sm:text-lg text-white hover:text-gray-300 transition-colors cursor-pointer break-all">ernado@outlook.com.tr</a>
              </div>

              <h4 className="text-xs sm:text-sm uppercase tracking-widest mb-3 sm:mb-4" style={{ letterSpacing: '2px' }}>
                {t('footer.address')}
              </h4>
              <p className="text-sm sm:text-base text-[#B0B0B0] leading-relaxed">
                {t('footer.addressText')}
              </p>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-white/10 mb-6 sm:mb-8"></div>

          {/* Bottom Section */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            {/* Social Links */}
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 sm:gap-6 lg:gap-8">
              {[
                { name: 'Instagram', icon: 'ri-instagram-line', url: 'https://www.instagram.com/ernadoproject?igsh=OXU0bmltNWFwaTVs' },
                { name: 'LinkedIn', icon: 'ri-linkedin-line', url: '#' },
                { name: 'Pinterest', icon: 'ri-pinterest-line', url: '#' },
                { name: 'Behance', icon: 'ri-behance-line', url: '#' }
              ].map((social) => (
                <a
                  key={social.name}
                  href={social.url}
                  target={social.url !== '#' ? '_blank' : undefined}
                  rel={social.url !== '#' ? 'noopener noreferrer' : undefined}
                  className="text-white/70 hover:text-white transition-colors underline text-xs sm:text-sm cursor-pointer flex items-center gap-2"
                >
                  <i className={`${social.icon} text-base sm:text-lg w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center`}></i>
                  {social.name}
                </a>
              ))}
            </div>

            {/* Copyright */}
            <div className="text-xs sm:text-sm text-white/70 text-center order-last md:order-none">
              © 2025 Ernado. {t('footer.rights')} • <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">Website Builder</a>
            </div>

            {/* Policy Links */}
            <div className="flex items-center gap-3 sm:gap-4 text-xs sm:text-sm">
              <button 
                onClick={() => setShowPrivacyModal(true)}
                className="text-white/70 hover:text-white transition-colors underline cursor-pointer whitespace-nowrap"
              >
                Privacy
              </button>
              <span className="text-white/30">|</span>
              <button 
                onClick={() => setShowTermsModal(true)}
                className="text-white/70 hover:text-white transition-colors underline cursor-pointer whitespace-nowrap"
              >
                Terms
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Privacy Policy Modal */}
      {showPrivacyModal && (
        <div 
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => setShowPrivacyModal(false)}
        >
          <div 
            className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-[#D4AF37] to-[#C9A961] px-8 py-6 flex items-center justify-between rounded-t-3xl">
              <h2 className="text-3xl font-light text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                {t('privacy.title')}
              </h2>
              <button
                onClick={() => setShowPrivacyModal(false)}
                className="w-10 h-10 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl text-white w-6 h-6 flex items-center justify-center"></i>
              </button>
            </div>

            {/* Content */}
            <div className="px-8 py-8 space-y-6 text-gray-800">
              <p className="text-base leading-relaxed">{t('privacy.intro')}</p>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-[#D4AF37]">{t('privacy.section1Title')}</h3>
                <p className="text-base leading-relaxed">{t('privacy.section1Text')}</p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-[#D4AF37]">{t('privacy.section2Title')}</h3>
                <p className="text-base leading-relaxed">{t('privacy.section2Text')}</p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-[#D4AF37]">{t('privacy.section3Title')}</h3>
                <p className="text-base leading-relaxed">{t('privacy.section3Text')}</p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-[#D4AF37]">{t('privacy.section4Title')}</h3>
                <p className="text-base leading-relaxed">{t('privacy.section4Text')}</p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-[#D4AF37]">{t('privacy.section5Title')}</h3>
                <p className="text-base leading-relaxed">{t('privacy.section5Text')}</p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-[#D4AF37]">{t('privacy.section6Title')}</h3>
                <p className="text-base leading-relaxed">{t('privacy.section6Text')}</p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-[#D4AF37]">{t('privacy.section7Title')}</h3>
                <p className="text-base leading-relaxed">{t('privacy.section7Text')}</p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-[#D4AF37]">{t('privacy.section8Title')}</h3>
                <p className="text-base leading-relaxed">{t('privacy.section8Text')}</p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-[#D4AF37]">{t('privacy.section9Title')}</h3>
                <p className="text-base leading-relaxed">{t('privacy.section9Text')}</p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-[#D4AF37]">{t('privacy.section10Title')}</h3>
                <p className="text-base leading-relaxed">{t('privacy.section10Text')}</p>
              </div>

              <div className="pt-4 border-t-2 border-[#D4AF37]/30 mt-8">
                <h3 className="text-2xl font-semibold mb-4 text-[#D4AF37]">{t('privacy.cookieTitle')}</h3>
                <p className="text-base leading-relaxed mb-6">{t('privacy.cookieText')}</p>

                <div className="space-y-4">
                  <div>
                    <h4 className="text-lg font-semibold mb-2 text-[#D4AF37]">{t('privacy.cookieSection1Title')}</h4>
                    <p className="text-base leading-relaxed">{t('privacy.cookieSection1Text')}</p>
                  </div>

                  <div>
                    <h4 className="text-lg font-semibold mb-2 text-[#D4AF37]">{t('privacy.cookieSection2Title')}</h4>
                    <p className="text-base leading-relaxed">{t('privacy.cookieSection2Text')}</p>
                  </div>

                  <div>
                    <h4 className="text-lg font-semibold mb-2 text-[#D4AF37]">{t('privacy.cookieSection3Title')}</h4>
                    <p className="text-base leading-relaxed">{t('privacy.cookieSection3Text')}</p>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t-2 border-[#D4AF37]/30 mt-8">
                <h3 className="text-2xl font-semibold mb-4 text-[#D4AF37]">{t('privacy.kvkkTitle')}</h3>
                <p className="text-base leading-relaxed mb-6">{t('privacy.kvkkIntro')}</p>

                <div className="space-y-4">
                  <div>
                    <h4 className="text-lg font-semibold mb-2 text-[#D4AF37]">{t('privacy.kvkkDataTitle')}</h4>
                    <p className="text-base leading-relaxed">{t('privacy.kvkkDataText')}</p>
                  </div>

                  <div>
                    <h4 className="text-lg font-semibold mb-2 text-[#D4AF37]">{t('privacy.kvkkPurposeTitle')}</h4>
                    <p className="text-base leading-relaxed">{t('privacy.kvkkPurposeText')}</p>
                  </div>

                  <div>
                    <h4 className="text-lg font-semibold mb-2 text-[#D4AF37]">{t('privacy.kvkkLegalTitle')}</h4>
                    <p className="text-base leading-relaxed">{t('privacy.kvkkLegalText')}</p>
                  </div>

                  <div>
                    <h4 className="text-lg font-semibold mb-2 text-[#D4AF37]">{t('privacy.kvkkRightsTitle')}</h4>
                    <p className="text-base leading-relaxed">{t('privacy.kvkkRightsText')}</p>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600">{t('privacy.contact')}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Terms of Use Modal */}
      {showTermsModal && (
        <div 
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => setShowTermsModal(false)}
        >
          <div 
            className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-[#D4AF37] to-[#C9A961] px-8 py-6 flex items-center justify-between rounded-t-3xl">
              <h2 className="text-3xl font-light text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                {t('terms.title')}
              </h2>
              <button
                onClick={() => setShowTermsModal(false)}
                className="w-10 h-10 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl text-white w-6 h-6 flex items-center justify-center"></i>
              </button>
            </div>

            {/* Content */}
            <div className="px-8 py-8 space-y-6 text-gray-800">
              <p className="text-base leading-relaxed">{t('terms.intro')}</p>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-[#D4AF37]">{t('terms.section1Title')}</h3>
                <p className="text-base leading-relaxed">{t('terms.section1Text')}</p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-[#D4AF37]">{t('terms.section2Title')}</h3>
                <p className="text-base leading-relaxed">{t('terms.section2Text')}</p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-[#D4AF37]">{t('terms.section3Title')}</h3>
                <p className="text-base leading-relaxed">{t('terms.section3Text')}</p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-[#D4AF37]">{t('terms.section4Title')}</h3>
                <p className="text-base leading-relaxed">{t('terms.section4Text')}</p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 text-[#D4AF37]">{t('terms.section5Title')}</h3>
                <p className="text-base leading-relaxed">{t('terms.section5Text')}</p>
              </div>

              <div className="pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600">{t('terms.section6Text')}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
